/** Rubrique Exporter : stock, domaines, journal, carte, sauvegarde JSON + export auto mensuel. */
import { useState, useEffect } from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { metaApi, parametresApi, vignoblesApi } from '../../../services/api';
import { usePublicMode } from '../../../context/PublicModeContext';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card } from '../../../components/ui/kit';

const EXPORTS = [
  { kind: 'stock', title: 'Stock et inventaire', desc: 'Une ligne par référence : cuvée, domaine, millésime, format, couleur, emplacement, qté, achat HT, valeur.' },
  { kind: 'domaines', title: 'Domaines connus', desc: 'Domaine, région, adresse, contact, site, statut, réf., btl.' },
  { kind: 'journal', title: 'Journal des entrées et sorties', desc: 'Date, heure, utilisateur, type, référence, motif, qté, prix.' },
  { kind: 'carte', title: 'Carte des vins (données)', desc: 'Section, cuvée, appellation, description, prix.' },
];

export default function Exporter() {
  const { isPublic } = usePublicMode();
  const { user } = useAuth();
  const [est, setEst] = useState(null);
  useEffect(() => { metaApi.settings().then(setEst); }, []);

  const dl = async (kind, fmt) => { try { await parametresApi.download(kind, fmt, isPublic); } catch { toast.error('Échec export'); } };
  const saveAuto = async () => { await parametresApi.updateEstablishment(est); toast.success('Export automatique enregistré'); };

  return (
    <div className="max-w-[760px]">
      <h2 className="font-title text-3xl mb-1">Exporter</h2>
      <p className="text-[13px] text-n600 mb-5 max-w-[620px]">Fichiers pour Excel, le comptable ou une sauvegarde. Les exports respectent le mode écran public : sans prix d'achat si l'œil est fermé.</p>
      <div className="space-y-3" data-testid="exports-list">
        {EXPORTS.map((e) => (
          <Card key={e.kind} className="p-4 flex items-center gap-4" data-testid={`export-${e.kind}`}>
            <div className="flex-1"><p className="font-title text-lg">{e.title}</p><p className="text-[12.5px] text-n600">{e.desc}</p></div>
            <Button variant="secondary" className="h-9" onClick={() => dl(e.kind, 'xlsx')} data-testid={`export-${e.kind}-xlsx`}><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
            <Button variant="ghost" className="h-9" onClick={() => dl(e.kind, 'csv')} data-testid={`export-${e.kind}-csv`}><Download className="w-4 h-4" /> CSV</Button>
          </Card>
        ))}
        {user?.role === 'gerant' && (
          <Card className="p-4 flex items-center gap-4" data-testid="export-sauvegarde">
            <div className="flex-1"><p className="font-title text-lg">Sauvegarde complète</p><p className="text-[12.5px] text-n600">JSON de toutes les collections (gérant).</p></div>
            <Button variant="secondary" className="h-9" onClick={() => dl('sauvegarde', 'json')} data-testid="export-backup"><Download className="w-4 h-4" /> Télécharger</Button>
          </Card>
        )}
        <Card className="p-4 flex items-center gap-4" data-testid="export-appellations">
          <div className="flex-1"><p className="font-title text-lg">Index des appellations</p><p className="text-[12.5px] text-n600">Toutes les appellations (pays, vignoble, sous-région, couleur, cépages, remarques), tous pays confondus.</p></div>
          <Button variant="secondary" className="h-9" onClick={() => vignoblesApi.exportAppellations('json').catch(() => toast.error('Échec export'))} data-testid="export-appellations-json"><Download className="w-4 h-4" /> JSON</Button>
          <Button variant="ghost" className="h-9" onClick={() => vignoblesApi.exportAppellations('csv').catch(() => toast.error('Échec export'))} data-testid="export-appellations-csv"><Download className="w-4 h-4" /> CSV</Button>
        </Card>
      </div>

      {user?.role === 'gerant' && est && (
        <Card className="p-4 mt-5" data-testid="auto-export">
          <p className="font-title text-lg mb-2">Export automatique mensuel</p>
          <p className="text-[12.5px] text-n600 mb-3">Chaque 1er du mois, le stock et le journal sont envoyés par email.</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" className="accent-gold" checked={est.auto_export_enabled || false} onChange={(e) => setEst({ ...est, auto_export_enabled: e.target.checked })} data-testid="auto-export-toggle" /> Activer</label>
            <input className="vd-input h-9 w-[260px]" placeholder="Email destinataire" value={est.auto_export_email || ''} onChange={(e) => setEst({ ...est, auto_export_email: e.target.value })} data-testid="auto-export-email" />
            <Button variant="primary" className="h-9" onClick={saveAuto} data-testid="auto-export-save">Enregistrer</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
